// src/pages/UsersPage.jsx
import React from "react";
import {
    Paper, TextField, TableContainer, Table, TableHead, TableRow,
    TableCell, TableBody, Button, Box, CircularProgress, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions,
    MenuItem, Select, InputLabel, FormControl, Checkbox, FormControlLabel,
    IconButton, Tooltip, TableSortLabel, Typography
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import PageContainer from "../layout/PageContainer";
import useUsers from "../../hooks/useUsers";
import useCompanies from "../../hooks/useCompanies";

// -------- Helpers für Sortierung --------
function descendingComparator(a, b, orderBy) {
    const va = getCellValue(a, orderBy);
    const vb = getCellValue(b, orderBy);

    const na = typeof va === "number" ? va : Number.isFinite(Number(va)) ? Number(va) : null;
    const nb = typeof vb === "number" ? vb : Number.isFinite(Number(vb)) ? Number(vb) : null;

    if (na !== null && nb !== null) {
        if (nb < na) return -1;
        if (nb > na) return 1;
        return 0;
    }

    const sa = (va ?? "").toString().toLowerCase();
    const sb = (vb ?? "").toString().toLowerCase();
    if (sb < sa) return -1;
    if (sb > sa) return 1;
    return 0;
}

function getComparator(order, orderBy) {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilized = array.map((el, index) => [el, index]);
    stabilized.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilized.map((el) => el[0]);
}

function getCellValue(u, key) {
    switch (key) {
        case "name":
            return `${u.firstname || ""} ${u.lastname || ""}`.trim();
        case "username":
            return u.username || "";
        case "email":
            return u.email || "";
        case "role":
            return u.role || "";
        case "company":
            return u.company_name || "";
        case "id":
            return u.id ?? null;
        default:
            return "";
    }
}

export default function UsersPage() {
    const [query, setQuery] = React.useState("");

    const { users, loading, error, fetchUsers, createUser, updateUser, deleteUser } = useUsers();
    const { companies, fetchCompanies } = useCompanies();

    React.useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

    const [openCreate, setOpenCreate] = React.useState(false);
    const [openEdit, setOpenEdit]     = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [submitError, setSubmitError] = React.useState("");
    const [editingId, setEditingId]   = React.useState(null);

    // NEW: Delete Dialog state
    const [openDelete, setOpenDelete] = React.useState(false);
    const [deleteTarget, setDeleteTarget] = React.useState(null);
    const [deleting, setDeleting] = React.useState(false);
    const [deleteError, setDeleteError] = React.useState("");

    const [order, setOrder] = React.useState("asc");
    const [orderBy, setOrderBy] = React.useState("name");

    const blankForm = {
        username: "", email: "", password: "", passwordConfirm: "",
        firstname: "", lastname: "", role: "user", company_id: null, is_active: true,
    };
    const [form, setForm] = React.useState(blankForm);

    const onChange = (key) => (e) => {
        const value = e.target?.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((f) => ({ ...f, [key]: value }));
    };

    const openCreateDialog = () => { setForm(blankForm); setSubmitError(""); setOpenCreate(true); };
    const closeCreateDialog = () => { if (!submitting) setOpenCreate(false); };

    const openEditDialog = (u) => {
        setEditingId(u.id);
        setForm({
            username: u.username || "",
            email:    u.email || "",
            password: "",
            passwordConfirm: "",
            firstname: u.firstname || "",
            lastname:  u.lastname || "",
            role:      u.role || "user",
            company_id: u.company_id ?? null,
            is_active: !!u.is_active,
        });
        setSubmitError("");
        setOpenEdit(true);
    };
    const closeEditDialog = () => { if (!submitting) setOpenEdit(false); };

    const handleCreate = async () => {
        setSubmitError("");
        if (!form.username?.trim() || !form.email?.trim() || !form.password) {
            setSubmitError("Bitte Benutzername, E-Mail und Passwort ausfüllen.");
            return;
        }
        if (form.password !== form.passwordConfirm) {
            setSubmitError("Passwörter stimmen nicht überein.");
            return;
        }
        setSubmitting(true);
        try {
            await createUser({
                username: form.username.trim(),
                email: form.email.trim(),
                password: form.password,
                firstname: form.firstname?.trim() || null,
                lastname: form.lastname?.trim() || null,
                role: form.role,
                company_id: form.company_id ?? null,
                is_active: form.is_active,
            });
            setOpenCreate(false);
        } catch (e) {
            setSubmitError(e.message || "Erstellen fehlgeschlagen.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = async () => {
        setSubmitError("");
        if (!editingId) return;

        if (!form.username?.trim() || !form.email?.trim()) {
            setSubmitError("Bitte Benutzername und E-Mail ausfüllen.");
            return;
        }
        if (form.password || form.passwordConfirm) {
            if (form.password !== form.passwordConfirm) {
                setSubmitError("Passwörter stimmen nicht überein.");
                return;
            }
        }

        const payload = {
            username: form.username.trim(),
            email:    form.email.trim(),
            firstname: form.firstname?.trim() || null,
            lastname:  form.lastname?.trim() || null,
            role: form.role,
            company_id: form.company_id ?? null,
            is_active: form.is_active,
        };
        if (form.password) payload.password = form.password;

        setSubmitting(true);
        try {
            await updateUser(editingId, payload);
            setOpenEdit(false);
        } catch (e) {
            setSubmitError(e.message || "Aktualisierung fehlgeschlagen.");
        } finally {
            setSubmitting(false);
        }
    };

    // Filter
    const filtered = React.useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return users;
        return users.filter((u) =>
            (`${u.firstname || ""} ${u.lastname || ""}`.trim()).toLowerCase().includes(q) ||
            (u.username || "").toLowerCase().includes(q) ||
            (u.email || "").toLowerCase().includes(q) ||
            (u.role || "").toLowerCase().includes(q) ||
            (u.company_name || "").toLowerCase().includes(q)
        );
    }, [users, query]);

    // Sortiert + gefiltert
    const rows = React.useMemo(() => {
        return stableSort(filtered, getComparator(order, orderBy));
    }, [filtered, order, orderBy]);

    // Sort-Handler
    const handleRequestSort = (property) => () => {
        setOrder((prevOrder) => (orderBy === property && prevOrder === "asc" ? "desc" : "asc"));
        setOrderBy(property);
    };

    const columns = [
        { id: "name",      label: "Name" },
        { id: "username",  label: "Benutzername" },
        { id: "email",     label: "E-Mail" },
        { id: "role",      label: "Rolle" },
        { id: "company",   label: "Firma" },
    ];

    // NEW: Delete handlers
    const askDelete = (user) => {
        setDeleteTarget(user);
        setDeleteError("");
        setOpenDelete(true);
    };
    const closeDeleteDialog = () => {
        if (!deleting) {
            setOpenDelete(false);
            setDeleteTarget(null);
            setDeleteError("");
        }
    };
    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        setDeleteError("");
        try {
            await deleteUser(deleteTarget.id);
            setOpenDelete(false);
            setDeleteTarget(null);
        } catch (e) {
            setDeleteError(e?.message || "Löschen fehlgeschlagen.");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <PageContainer
            title="Nutzerverwaltung"
            breadcrumbs={["Home", "Nutzer"]}
            actions={
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="outlined" onClick={fetchUsers} disabled={loading}>Aktualisieren</Button>
                    <Button variant="contained" onClick={openCreateDialog}>Neuen Nutzer anlegen</Button>
                </Box>
            }
        >
            <Paper sx={{ p: 2, mb: 2 }}>
                <TextField
                    placeholder="Suchen nach Name, Benutzername, E-Mail, Rolle, Firma…"
                    fullWidth
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                    <Button color="inherit" size="small" onClick={fetchUsers} sx={{ ml: 1 }}>
                        Erneut laden
                    </Button>
                </Alert>
            )}

            <TableContainer component={Paper}>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                {columns.map((col) => (
                                    <TableCell
                                        key={col.id}
                                        sortDirection={orderBy === col.id ? order : false}
                                        sx={{ userSelect: "none" }}
                                    >
                                        <TableSortLabel
                                            active={orderBy === col.id}
                                            direction={orderBy === col.id ? order : "asc"}
                                            onClick={handleRequestSort(col.id)}
                                        >
                                            {col.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                                <TableCell align="right">Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((u) => (
                                <TableRow key={u.id} hover>
                                    <TableCell>{`${u.firstname || ""} ${u.lastname || ""}`.trim() || "-"}</TableCell>
                                    <TableCell>{u.username || "-"}</TableCell>
                                    <TableCell>{u.email || "-"}</TableCell>
                                    <TableCell>{u.role || "-"}</TableCell>
                                    <TableCell>{u.company_name || "-"}</TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Bearbeiten">
                                            <IconButton onClick={() => openEditDialog(u)} size="small" sx={{ mr: 0.5 }}>
                                                <EditIcon fontSize="inherit" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Löschen">
                                            <IconButton
                                                color="error"
                                                onClick={() => askDelete(u)}
                                                size="small"
                                            >
                                                <DeleteIcon fontSize="inherit" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!rows.length && !loading && (
                                <TableRow>
                                    <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 6 }}>
                                        Keine Nutzer gefunden.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            {/* Create Dialog */}
            <Dialog open={openCreate} onClose={closeCreateDialog} fullWidth maxWidth="sm">
                <DialogTitle>Neuen Nutzer anlegen</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mt: 1 }}>
                        <TextField label="Benutzername*" value={form.username} onChange={onChange("username")} />
                        <TextField label="E-Mail*" type="email" value={form.email} onChange={onChange("email")} />
                        <TextField label="Vorname" value={form.firstname} onChange={onChange("firstname")} />
                        <TextField label="Nachname" value={form.lastname} onChange={onChange("lastname")} />

                        <FormControl>
                            <InputLabel id="role-label-create">Rolle</InputLabel>
                            <Select labelId="role-label-create" label="Rolle" value={form.role} onChange={onChange("role")}>
                                <MenuItem value="user">user</MenuItem>
                                <MenuItem value="editor">editor</MenuItem>
                                <MenuItem value="admin">admin</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl>
                            <InputLabel id="company-label-create">Firma</InputLabel>
                            <Select
                                labelId="company-label-create"
                                label="Firma"
                                value={form.company_id ?? ""}
                                onChange={(e) => setForm(f => ({ ...f, company_id: e.target.value || null }))}
                                displayEmpty
                            >
                                <MenuItem value="">– keine –</MenuItem>
                                {companies.map(c => (
                                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField label="Passwort*" type="password" value={form.password} onChange={onChange("password")} />
                        <TextField label="Passwort bestätigen*" type="password" value={form.passwordConfirm} onChange={onChange("passwordConfirm")} />

                        <Box sx={{ gridColumn: "1 / -1" }}>
                            <FormControlLabel control={<Checkbox checked={form.is_active} onChange={onChange("is_active")} />} label="Aktiv" />
                        </Box>
                    </Box>

                    {!!submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeCreateDialog} disabled={submitting}>Abbrechen</Button>
                    <Button onClick={handleCreate} variant="contained" disabled={submitting}>
                        {submitting ? "Speichern…" : "Speichern"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={openEdit} onClose={closeEditDialog} fullWidth maxWidth="sm">
                <DialogTitle>Nutzer bearbeiten</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mt: 1 }}>
                        <TextField label="Benutzername*" value={form.username} onChange={onChange("username")} />
                        <TextField label="E-Mail*" type="email" value={form.email} onChange={onChange("email")} />
                        <TextField label="Vorname" value={form.firstname} onChange={onChange("firstname")} />
                        <TextField label="Nachname" value={form.lastname} onChange={onChange("lastname")} />

                        <FormControl>
                            <InputLabel id="role-label-edit">Rolle</InputLabel>
                            <Select labelId="role-label-edit" label="Rolle" value={form.role} onChange={onChange("role")}>
                                <MenuItem value="user">user</MenuItem>
                                <MenuItem value="editor">editor</MenuItem>
                                <MenuItem value="admin">admin</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl>
                            <InputLabel id="company-label-edit">Firma</InputLabel>
                            <Select
                                labelId="company-label-edit"
                                label="Firma"
                                value={form.company_id ?? ""}
                                onChange={(e) => setForm(f => ({ ...f, company_id: e.target.value || null }))}
                                displayEmpty
                            >
                                <MenuItem value="">– keine –</MenuItem>
                                {companies.map(c => (
                                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField label="Neues Passwort (optional)" type="password" value={form.password} onChange={onChange("password")} />
                        <TextField label="Passwort bestätigen" type="password" value={form.passwordConfirm} onChange={onChange("passwordConfirm")} />

                        <Box sx={{ gridColumn: "1 / -1" }}>
                            <FormControlLabel control={<Checkbox checked={form.is_active} onChange={onChange("is_active")} />} label="Aktiv" />
                        </Box>
                    </Box>

                    {!!submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeEditDialog} disabled={submitting}>Abbrechen</Button>
                    <Button onClick={handleEdit} variant="contained" disabled={submitting}>
                        {submitting ? "Speichern…" : "Speichern"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* NEW: Delete Confirm Dialog */}
            <Dialog open={openDelete} onClose={closeDeleteDialog} fullWidth maxWidth="xs">
                <DialogTitle>Nutzer wirklich löschen?</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body1" gutterBottom>
                        Möchtest du den folgenden Nutzer endgültig löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.
                    </Typography>
                    {deleteTarget && (
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="subtitle2">Name</Typography>
                            <Typography gutterBottom>
                                {`${deleteTarget.firstname || ""} ${deleteTarget.lastname || ""}`.trim() || "–"}
                            </Typography>
                            <Typography variant="subtitle2">Benutzername</Typography>
                            <Typography gutterBottom>{deleteTarget.username || "–"}</Typography>
                            <Typography variant="subtitle2">E-Mail</Typography>
                            <Typography>{deleteTarget.email || "–"}</Typography>
                        </Box>
                    )}
                    {!!deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteDialog} disabled={deleting}>Abbrechen</Button>
                    <Button onClick={confirmDelete} color="error" variant="contained" disabled={deleting}>
                        {deleting ? "Lösche…" : "Löschen"}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
}
